import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AUTO_LOAD_CONTROL } from '../config-control';
import {
  BACKGROUND_CREATE_MODE,
  CONSULT_MODE,
  CREATE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_MODE,
  UPDATE_MODE,
} from '../mode';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  PREPARING_ACTION_STATUS,
  SUCCESS_STATUS,
} from '../status';
import {
  deleteById, fetchAll, fetchByFilter, fetchById, inactivateById,
} from './perfilAPI';
import i18n from '../../i18n';

const initialState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
  },
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  list: [],
  config: { control: AUTO_LOAD_CONTROL },
  model: {
    id: null,
    nome: null,
    atributos: {
      unidadesDeNegocio: [],
      clientes: [],
      followups: [],
      grupo: [{ id: '' }],
    },
    permissoesAcesso: [],
  },
};

const fetchAllAsync = createAsyncThunk(
  'perfil/fetchAll',
  async () => {
    const { data } = await fetchAll();
    return { data };
  }
);

const fetchByFilterAsync = createAsyncThunk(
  'perfil/fetchByFilter',
  async (params) => {
    const { data } = await fetchByFilter(params);
    return { data };
  }
);
const fetchByIdAsync = createAsyncThunk(
  'perfil/fetchById',
  async (data) => {
    const response = await fetchById(data);
    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const deleteByIdAsync = createAsyncThunk(
  'perfil/deleteById',
  async (id) => {
    const { status, data } = await deleteById(id);
    return { response: { status, data } };
  }
);
const inactivateByIdAsync = createAsyncThunk(
  'perfil/inactivateById',
  async (id) => {
    const { status, data } = await inactivateById(id);
    return { response: { status, data } };
  }
);
const perfilSlice = createSlice({
  name: 'perfil',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    preparingAction: (state) => {
      state.status = PREPARING_ACTION_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    setModel: (state, action) => {
      state.model = {
        ...action.payload,
      };
    },
    setUnidadesNegocio: (state, action) => {
      state.model.atributos.unidadesDeNegocio = [...action.payload];
    },
    setPermissoesAcesso: (state, action) => {
      state.model.permissoesAcesso = [...action.payload];
    },
    setClientes: (state, action) => {
      state.model.atributos.clientes = [...action.payload];
    },
    setFollowups: (state, action) => {
      state.model.atributos.followups = [...action.payload];
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    resetList: (state) => {
      state.list = [];
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = initialState.error;
    },
    setConfig: (state, action) => {
      state.config = action.payload;
    },
    changeControlTo: (state, action) => {
      state.config.control = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.carregarListaPerfis', { erro: action.error.message }),
        };
      })
      .addCase(fetchByFilterAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchByFilterAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchByFilterAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.carregarListaPerfis', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          state.model = action.payload.response.data;
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.carregarDadosPerfil', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { nome } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.perfilExcluido', { perfil: nome });

        if (state.response.status === 200) {
          state.list = state.list
            .filter((current) => (
              current.id !== action.payload.response.data.id
            ));
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.excluirPerfil', { erro: action.error.message }),
        };
      })
      .addCase(deleteByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(deleteByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { nome } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.perfilExcluido', { perfil: nome });

        if (state.response.status === 200) {
          state.list = state.list
            .filter((current) => (
              current.id !== action.payload.response.data.id
            ));
        }
      })
      .addCase(deleteByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.excluirPerfil', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  preparingAction,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  setModel,
  setClientes,
  setFollowups,
  setUnidadesNegocio,
  setPermissoesAcesso,
  resetModel,
  addToList,
  updateOnList,
  resetList,
  setConfig,
  setResponse,
  clearResponse,
  setError,
  clearError,
} = perfilSlice.actions;

const perfilActions = perfilSlice.actions;

const selectPerfis = (state) => state.perfil.list;
const selectMode = (state) => state.perfil.mode.main;
const selectBackgroundMode = (state) => state.perfil.mode.background;
const selectStatus = (state) => state.perfil.status;
const selectError = (state) => state.perfil.error;
const selectResponse = (state) => state.perfil.response;
const selectPerfil = (state) => state.perfil.model;
const selectConfigControl = (state) => state.perfil.config.control;

export {
  perfilSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  preparingAction,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  setModel,
  setUnidadesNegocio,
  setPermissoesAcesso,
  resetModel,
  addToList,
  updateOnList,
  resetList,
  setConfig,
  setResponse,
  clearResponse,
  setError,
  clearError,
  fetchAllAsync,
  fetchByFilterAsync,
  fetchByIdAsync,
  deleteByIdAsync,
  inactivateByIdAsync,
  perfilActions,
  setClientes,
  setFollowups,
  selectPerfis,
  selectPerfil,
  selectStatus,
  selectMode,
  selectBackgroundMode,
  selectError,
  selectResponse,
  selectConfigControl,
};

export default perfilSlice.reducer;
